import React, { useEffect, useEffectEvent, useRef } from 'react';

import { AlertDialog, addWithConfig, makeUclComponent } from '@rbilabs/universal-components';
import { Platform, StyleSheet } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { Box } from 'components/ui/box';
import { Typography } from 'components/ui/typography';
import { t } from 'state/intl/translations';
import noop from 'utils/noop';

import { AlertDialogEventLogger } from './alert-dialog-event-logger';
import type { IDialogProps } from './types';

// NativeBase's AlertDialog keeps its underlying RN <Modal visible={true}> mounted for
// NATIVEBASE_MODAL_EXIT_DURATION_MS after isOpen=false (Fade exitDuration defined in
// node_modules/native-base/src/components/composites/AlertDialog/AlertDialog.tsx). On iOS,
// presenting a second Modal during that window causes the presentation to be silently dropped.
// InteractionManager.runAfterInteractions() cannot help because NativeBase's Transition uses
// Animated.timing with useNativeDriver:true, which runs on the native thread and does not
// register with InteractionManager.
const NATIVEBASE_MODAL_EXIT_DURATION_MS = 150;
const MODAL_SEQUENCE_DELAY_MS =
  Platform.OS === 'ios'
    ? NATIVEBASE_MODAL_EXIT_DURATION_MS + 50 // +50ms buffer for JS-thread scheduling variance
    : 0;

export const CloseButton = makeUclComponent(ActionButton).withConfig({
  fullWidth: true,
  variant: ActionButtonVariants.TEXT_ONLY,
});

const Dialog: React.FC<React.PropsWithChildren<IDialogProps>> = ({
  children,
  heading,
  headingComponent,
  body,
  bodyComponent,
  onDismiss,
  onAfterClose,
  actions,
  image,
  initialFocusRef,
  /**
   * A small string sent to amplitude describing the purpose of the modal.
   */
  modalAppearanceEventMessage,
  /**
   * Additional Error details to be sent to amplitude.
   */
  errorEventMessage,
  testID,
  showCloseButton = false,
  showDialog,
  confirmButtonLabel,
  closeButtonLabelId = 'close',
  closeButtonDefaultMessage = 'Close',
  confirmDialog = noop,
  maxHeight,
  contentWidth,
  contentMaxWidth,
  marginBottom,
  contentPadding,
  closeButtonVariant = ActionButtonVariants.TEXT_ONLY,
  ...props
}) => {
  if (confirmButtonLabel && !actions) {
    actions = (
      <ActionButton
        fullWidth={showCloseButton}
        onPress={confirmDialog}
        testID="dialog-confirm-btn"
        variant={showCloseButton ? ActionButtonVariants.PRIMARY : ActionButtonVariants.TEXT_ONLY}
      >
        {confirmButtonLabel}
      </ActionButton>
    );
  }

  if (!actions && !showCloseButton && __DEV__) {
    // eslint-disable-next-line no-console
    console.error('Dialog: Please add actions or show the close button (showCloseButton)');
  }
  const cancelRef = React.useRef(null);

  const onAfterCloseEvent = useEffectEvent(() => onAfterClose?.());

  // Fire onAfterClose only on open→closed transitions, never on initial mount.
  const prevShowDialogRef = useRef(showDialog);
  useEffect(() => {
    const didJustClose = prevShowDialogRef.current && !showDialog;
    prevShowDialogRef.current = showDialog;

    if (didJustClose) {
      const timer = setTimeout(onAfterCloseEvent, MODAL_SEQUENCE_DELAY_MS);
      return () => clearTimeout(timer);
    }
    // onAfterCloseEvent is an Effect Event and must stay out of dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDialog]);

  // The dialog content will respect its default maximum height unless you specify a different maxHeight value.
  const dialogContentMaxHeight = { ...(maxHeight && { maxHeight }) };
  const dialogContentWidth = {
    ...(contentWidth && {
      width: contentWidth,
    }),
  };

  return (
    <AlertDialogEventLogger
      modalAppearanceEventMessage={modalAppearanceEventMessage}
      errorEventMessage={errorEventMessage}
      modalAppearanceHeader={heading}
      modalAppearanceMessage={body}
      leastDestructiveRef={cancelRef}
      isOpen={showDialog}
      onClose={onDismiss}
      {...props}
    >
      <AlertDialog.Content
        {...dialogContentWidth}
        {...dialogContentMaxHeight}
        testID={testID}
        maxWidth={contentMaxWidth}
        marginBottom={marginBottom}
        padding={contentPadding}
      >
        {image}
        {headingComponent ? (
          headingComponent
        ) : heading ? (
          <AlertDialog.Header accessibilityLabel={`${heading}: ${body}`}>
            <Typography variant="headerTwo" accessibilityHidden>
              {heading}
            </Typography>
          </AlertDialog.Header>
        ) : null}
        {children ? (
          children
        ) : bodyComponent ? (
          bodyComponent
        ) : body ? (
          <AlertDialog.Body>{body}</AlertDialog.Body>
        ) : null}
        {(actions || showCloseButton) && (
          <AlertDialog.Footer>
            {actions}
            {showCloseButton && (
              <Box style={styles.closeButtonContainer}>
                <CloseButton onPress={onDismiss} variant={closeButtonVariant}>
                  {t(closeButtonLabelId, closeButtonDefaultMessage)}
                </CloseButton>
              </Box>
            )}
          </AlertDialog.Footer>
        )}
      </AlertDialog.Content>
    </AlertDialogEventLogger>
  );
};

export default addWithConfig(React.memo(Dialog));

const styles = StyleSheet.create({
  closeButtonContainer: {
    marginTop: 8,
    width: '100%',
  },
});
